import {
  NotificationContentInput,
  SchedulableTriggerInputTypes,
  scheduleNotificationAsync,
} from 'expo-notifications';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemTrailingAccessoryProps } from 'shared/components/vxl/ListItem/ListItemTrailingAccessory';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { TagType } from 'shared/components/vxl/Tag/Tag';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import { VXLComponentId } from 'shared/renderer/types';
import { offlineSimulatorVisibleAtom } from 'shared/state/SandboxAtoms';
import theme from 'shared/styles/theme';

interface SandboxItem {
  label: string;
  secondaryLabel: string;
  onPress: () => void;
  trailingAccessory?: ListItemTrailingAccessoryProps;
  featureFlag?: FeatureFlagKey;
  isEnabled?: (params: {
    isFeatureEnabled: (flag: FeatureFlagKey) => boolean;
  }) => boolean;
}

const SECONDS_DELAY = 10;
const PAY_LATER_SANDBOX_EXPIRY_MS = 6 * 60 * 60 * 1000;

const SandboxScreen = () => {
  const router = useRouter();
  const { t } = useTranslation('myVaro', { keyPrefix: 'sandbox' });
  const { isFeatureEnabled } = useStatsig();
  const [offlineSimulatorVisible, setOfflineSimulatorVisible] = useAtom(
    offlineSimulatorVisibleAtom,
  );

  const testPushNotification = async () => {
    const notification: NotificationContentInput = {
      title: 'Rewards Expiring Soon',
      body: 'Your rewards are expiring soon. Check them out before they disappear!',
      data: {
        android_channel_id: 'varo-banking-app',
        url: 'https://bank.varomoney.com/rewards',
        notificationType: 'OFFERS_EXPIRING_SOON',
      },
    };
    try {
      await scheduleNotificationAsync({
        content: notification,
        trigger: {
          type: SchedulableTriggerInputTypes.TIME_INTERVAL,
          seconds: SECONDS_DELAY,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const sandboxItemConfigs: SandboxItem[] = [
    {
      label: 'Test Push Notification',
      secondaryLabel: `Schedules a test notification (${SECONDS_DELAY}s delay)`,
      onPress: () => {
        void testPushNotification();
      },
      trailingAccessory: {
        id: '2',
        name: VXLComponentId.TAG,
        props: {
          text: 'Debug',
          type: TagType.INFO,
        },
      },
    },
    {
      label: 'Pay Later — Intro',
      secondaryLabel: 'Pay Later first-time introduction',
      onPress: () => router.navigate('/pay-later'),
      featureFlag: FeatureFlagKey.ENABLE_PAY_LATER,
      trailingAccessory: {
        id: 'pay-later-intro',
        name: VXLComponentId.TAG,
        props: {
          text: 'In Dev',
          type: TagType.INFO,
        },
      },
    },
    {
      label: 'Pay Later — Set up success',
      secondaryLabel: 'Plan activated, expiring in 6 hours',
      onPress: () =>
        router.navigate({
          pathname: '/pay-later/set-up-success',
          params: {
            expiresAt: new Date(
              Date.now() + PAY_LATER_SANDBOX_EXPIRY_MS,
            ).toISOString(),
          },
        }),
      featureFlag: FeatureFlagKey.ENABLE_PAY_LATER,
    },
    {
      label: 'Pay Later — Set up error',
      secondaryLabel: 'Plan setup failed',
      onPress: () => router.navigate('/pay-later/set-up-error'),
      featureFlag: FeatureFlagKey.ENABLE_PAY_LATER,
    },
    {
      label: 'FBIT',
      secondaryLabel: 'Fee Based Instant Transfer',
      onPress: () =>
        router.navigate(
          '/move-money/transfer/instant-transfer?typeOfAccount=CHARGE_CARD',
        ),
      trailingAccessory: {
        id: 'debt-sale-account-closed',
        name: VXLComponentId.TAG,
        props: {
          text: 'In Dev',
          type: TagType.INFO,
        },
      },
    },
    {
      label: 'FBIT Transfer Review',
      secondaryLabel: 'Standalone URL-driven transfer review',
      onPress: () =>
        router.navigate('/move-money/transfer/review-transfer-details'),
    },
    {
      label: 'FBIT Transfer Processing',
      secondaryLabel: 'Uncertain transfer outcome screen',
      onPress: () =>
        router.navigate('/move-money/transfer/transfer-processing'),
    },
    {
      label: 'Network Error Simulator',
      secondaryLabel: 'Show draggable FAB for simulating network errors',
      onPress: () => setOfflineSimulatorVisible(!offlineSimulatorVisible),
      trailingAccessory: {
        id: '5',
        name: VXLComponentId.SWITCH,
        props: {
          isOn: offlineSimulatorVisible,
          onValueChange: (value: boolean) => setOfflineSimulatorVisible(value),
        },
      },
    },
    // Add future items here. Either provide featureFlag OR a custom isEnabled predicate.
  ];

  const sandboxItems: SandboxItem[] = sandboxItemConfigs
    .filter((item) => {
      const flagOk = item.featureFlag
        ? isFeatureEnabled(item.featureFlag)
        : true;
      const predicateOk = item.isEnabled
        ? item.isEnabled({ isFeatureEnabled })
        : true;
      return flagOk && predicateOk;
    })
    .map(({ featureFlag: _ff, isEnabled: _ie, ...rest }) => rest);

  return (
    <ScrollView style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: t('title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      {sandboxItems.map((item, i) => (
        <View key={item.label}>
          <ListItem
            primaryCopy={item.label}
            secondaryCopy={item.secondaryLabel}
            showsChevron
            trailingAccessory={item.trailingAccessory}
            onPress={() => item.onPress()}
          />
          {i < sandboxItems.length - 1 ? (
            <View style={styles.hairLineDivider}>
              <HairLineDivider />
            </View>
          ) : null}
        </View>
      ))}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexGrow: 1,
  },
  hairLineDivider: {
    paddingHorizontal: theme.spacing.m,
  },
});

export default SandboxScreen;
